/* eslint-disable no-param-reassign */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { addDays, format, isSameDay, parse } from 'date-fns';
import { Link } from 'react-router-dom';
import ReactHorizontalDatePicker from 'react-horizontal-strip-datepicker';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import * as Yup from 'yup';
import './ReactHorizontalDatePicker.css';

import Button from '../../../components/Button';
import Input from '../../../components/Input/index';
import { Main, Section, Calender } from './styles';
import api from '../../../services/api';
import { convertDateString } from '../../../utils/convertDateString';
import { confirmStatus, formatStatus } from '../../../utils/formatStatus';
import { useToast } from '../../../hooks/toast';
import getValidationErrors from '../../../utils/getValidationErrors';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ITodayOrdersDeliveryAddress {
  addressId: string;
  street: string;
  streetNumber: string;
  neighborhood: string;
  doorCode: string;
}

interface ITodayOrdersLunch {
  order: string;
  quantity: string;
  description: string;
}

interface ITodayOrders {
  id: string;
  formattedNumber: number;
  createdAt: any;
  deliveryDate: string;
  deliveryTime: string;
  client: string;
  apartment: string;
  additionalData: string;
  contactName?: string;
  contactPhone?: string;
  status:
  | 'A_CONFIRMAR'
  | 'COM_PENDENCIA'
  | 'EM_PRODUCAO'
  | 'FINALIZADO'
  | 'ENTREGUE'
  | 'CANCELADA';
  deliveryAddress: ITodayOrdersDeliveryAddress[];
  lunchs: ITodayOrdersLunch[];
}

interface IMapOfOrdersList {
  order: string;
  address: string;
  deliveryDate: string;
  quantity: number;
}

interface MapOfOrders {
  order: string;
  total: number;
  groupId: string;
  name: string;
  apartment: string;
  clientId: string;
  address: string;
  deliveryTime: string;
  additionalData: string;
  list: IMapOfOrdersList[];
}

interface IChangePasswordFormData {
  newPassword: string;
  confirmPassword: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

const DeliveryManDashBoard: React.FC = () => {
  const [allOrders, setAllOrders] = useState<ITodayOrders[]>([]);
  const [historyOrders, setHistoryOrders] = useState<ITodayOrders[]>([]);
  const [deliveryMap, setDeliveryMap] = useState<MapOfOrders[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [mapDate, setMapDate] = useState<Date>(new Date());
  const [startDate] = useState<Date>(new Date());
  const [endDate] = useState<Date>(addDays(new Date(), 10));

  // ─── Trocar Senha ───────────────────────────────────────────────────────
  const passwordFormRef = useRef<FormHandles>(null);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  const mountedRef = useRef(true);
  const { addToast } = useToast();

  const today = new Date();
  const todayStr = format(today, 'yyyy-MM-dd');
  const user = JSON.parse(localStorage.getItem('@BiB:user') || '{}');

  // ─── Modal de Contato ──────────────────────────────────────────────────
  const [selectedContact, setSelectedContact] = useState<{
    name?: string;
    phone?: string;
  } | null>(null);

  // ─── Derivados reativos ───────────────────────────────────────────────────

  const selectedDateStr = format(mapDate, 'yyyy-MM-dd');
  const isToday = selectedDateStr === todayStr;

  const selectedDayOrders = useMemo(
    () =>
      allOrders
        .filter(o => format(new Date(o.deliveryDate), 'yyyy-MM-dd') === selectedDateStr)
        .map(o => ({ ...o, formattedNumber: Number(o.deliveryTime?.split(':').join('') || 0) }))
        .sort((a, b) => a.formattedNumber - b.formattedNumber),
    [allOrders, selectedDateStr],
  );

  const nextOrders = useMemo(
    () =>
      allOrders
        .filter(o => {
          const d = format(new Date(o.deliveryDate), 'yyyy-MM-dd');
          const limitStr = format(addDays(mapDate, 7), 'yyyy-MM-dd');
          return d > selectedDateStr && d <= limitStr;
        })
        .sort((a, b) => Date.parse(a.deliveryDate) - Date.parse(b.deliveryDate)),
    [allOrders, selectedDateStr, mapDate],
  );

  // ─── Helpers ──────────────────────────────────────────────────────────────

  const parseDate = (date: string) => parse(date, 'yyyy-MM-dd', new Date());

  const filterDeliveryMap = (order: MapOfOrders, days: number) => {
    const match = order.list.filter(i =>
      isSameDay(parseDate(i.deliveryDate), addDays(startDate, days)),
    )[0];
    return match ? match.quantity : <span>0</span>;
  };

  // ─── Data fetching ────────────────────────────────────────────────────────

  const loadOrders = async () => {
    try {
      const { data } = await api.get<ITodayOrders[]>('food-order/my-orders');

      // ── LOGS DETALHADOS ──────────────────────────────────────────────────
      const entregues = data.filter(o => o.status === 'ENTREGUE');
      if (entregues.length === 0) {
        console.warn('Nenhum pedido ENTREGUE — o backend provavelmente filtra esses pedidos antes de retornar.');
      }
      // ────────────────────────────────────────────────────────────────────

      setAllOrders(data);

      const hist = data
        .filter(o => {
          const d = format(new Date(o.deliveryDate), 'yyyy-MM-dd');
          return (
            d < todayStr ||
            (d === todayStr && (o.status === 'ENTREGUE' || o.status === 'CANCELADA'))
          );
        })
        .sort((a, b) => Date.parse(b.deliveryDate) - Date.parse(a.deliveryDate));

      console.log(`📜 Pedidos que entrarão no histórico: ${hist.length}`, hist);
      setHistoryOrders(hist);

    } catch (err) {
      console.error('Erro ao chamar food-order/my-orders:', err);
      throw err;
    }
  };

  const loadDeliveryMap = async () => {
    try {
      const { data } = await api.get<MapOfOrders[]>(
        `food-order/day/range/${format(startDate, 'yyyy-MM-dd')}/${format(endDate, 'yyyy-MM-dd')}`,
      );
      setDeliveryMap(data);
    } catch {
      // silently ignore
    }
  };

  const didMount = async () => {
    setLoading(true);
    try {
      await Promise.all([loadOrders(), loadDeliveryMap()]);
    } catch {
      addToast({ type: 'error', title: 'Erro ao carregar pedidos', description: 'Não foi possível carregar seus pedidos.' });
    } finally {
      setLoading(false);
    }
  };

  // ─── Ação: marcar como entregue ───────────────────────────────────────────

  const handleMarkAsDelivered = async (orderId: string) => {
    try {
      await api.put(`food-order/update/status/${orderId}`, { status: 'ENTREGUE' });
      addToast({ type: 'success', title: 'Entrega confirmada!', description: 'Pedido marcado como entregue.' });

      const applyStatus = (orders: ITodayOrders[]) =>
        orders.map(o => (o.id === orderId ? { ...o, status: 'ENTREGUE' as const } : o));

      setAllOrders(prev => applyStatus(prev));
      setHistoryOrders(prev => applyStatus(prev));
    } catch (err: any) {
      addToast({
        type: 'error',
        title: 'Erro ao confirmar entrega',
        description: err?.request?.response
          ? JSON.parse(err.request.response).message
          : 'Ocorreu um erro ao atualizar o status.',
      });
    }
  };

  // ─── Ação: trocar a própria senha ──────────────────────────────────────────
  // Usa o endpoint de admin (PUT /control-panel/update/:id) que hoje aceita
  // qualquer usuário autenticado, já que o AuthGuard não filtra por role.
  // Busca os dados atuais primeiro pra reenviar tudo igual, só trocando a senha
  // (o validateUserUpdate no backend exige name, username e role no payload).

  const handleChangePassword = async (data: IChangePasswordFormData) => {
    try {
      passwordFormRef.current?.setErrors({});

      const schema = Yup.object().shape({
        newPassword: Yup.string()
          .min(6, 'Mínimo de 6 caracteres')
          .required('Informe a nova senha'),
        confirmPassword: Yup.string()
          .oneOf([Yup.ref('newPassword')], 'As senhas não coincidem')
          .required('Confirme a nova senha'),
      });

      await schema.validate(data, { abortEarly: false });

      setChangingPassword(true);

      const { data: current } = await api.get(`/control-panel/read/${user.id}`);

      await api.put(`/control-panel/update/${user.id}`, {
        name: current.name,
        username: current.username,
        job: current.job,
        contactNumber: current.contactNumber,
        role: current.userType,
        password: data.newPassword,
      });

      addToast({
        type: 'success',
        title: 'Senha alterada!',
        description: 'Sua senha foi atualizada com sucesso.',
      });

      passwordFormRef.current?.reset();
      setShowChangePassword(false);
      setChangingPassword(false);
    } catch (err: any) {
      setChangingPassword(false);

      if (err instanceof Yup.ValidationError) {
        passwordFormRef.current?.setErrors(getValidationErrors(err));
        return;
      }

      addToast({
        type: 'error',
        title: 'Erro ao trocar senha',
        description: err?.request?.response
          ? JSON.parse(err.request.response).message
          : 'Não foi possível alterar a senha.',
      });
    }
  };

  // ─── Lifecycle ────────────────────────────────────────────────────────────

  useEffect(() => {
    didMount();
    return () => { mountedRef.current = false; };
  }, []);

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <Main>

      <div className="header-main">
        <div>
          <h1>Olá, {user.name} — Seja bem vindo(a) de volta</h1>
          <h3>Acompanhe suas entregas</h3>
        </div>
        <Button type="button" onClick={() => setShowChangePassword(true)}>
          Trocar Senha
        </Button>
      </div>

      <Section>
        {/* ── Entregas do dia selecionado ───────────────────────────────────── */}
        <div className="sectionCard">
          <h2>
            Entregas do dia{' '}
            {isToday ? `hoje (${format(mapDate, 'dd/MM/yyyy')})` : format(mapDate, 'dd/MM/yyyy')}
          </h2>
          <div className="flex-container" style={{ display: 'grid', gridTemplateColumns: '0.8fr 1.2fr 1fr 0.8fr 0.8fr 1.5fr 1fr' }}>
            <div><h3>Horário</h3></div>
            <div><h3>Endereço</h3></div>
            <div><h3>Contato</h3></div>
            <div><h3>Apartamento</h3></div>
            <div><h3>Cód. Porta</h3></div>
            <div><h3>Pedido</h3></div>
            <div><h3>Status</h3></div>
          </div>

          {!loading && selectedDayOrders.length > 0 && selectedDayOrders.map(order => (
            <div key={order.id} className="flex-container" style={{ display: 'grid', gridTemplateColumns: '0.8fr 1.2fr 1fr 0.8fr 0.8fr 1.5fr 1fr' }}>
              <div>{order.deliveryTime?.substr(0, 5) || '--:--'}</div>
              <div>
                {order.deliveryAddress?.[0]
                  ? `${order.deliveryAddress[0].street}, n${order.deliveryAddress[0].streetNumber}`
                  : 'Endereço não informado'}
              </div>
              <div>
                {order.contactName || order.contactPhone ? (
                  <Button
                    type="button"
                    className="button-plus"
                    onClick={() => setSelectedContact({ name: order.contactName, phone: order.contactPhone })}
                  >
                    <span>+</span>
                  </Button>
                ) : (
                  <span style={{ color: '#bbb' }}>-</span>
                )}
              </div>
              <div>{order.apartment || '-'}</div>
              <div>{order.deliveryAddress?.[0]?.doorCode || '-'}</div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {order.lunchs?.length
                  ? order.lunchs.map((item, index) => (
                    <div key={String(index)}>{index < 3 ? `${item.quantity} - ${item.order}` : '...'}</div>
                  ))
                  : <span>Sem itens</span>}
              </div>
              <div>
                {order.status !== 'ENTREGUE' && order.status !== 'CANCELADA' ? (
                  <Button onClick={() => handleMarkAsDelivered(order.id)}>Entregue ✓</Button>
                ) : (
                  <div className={confirmStatus(order.status)}>
                    <span>{formatStatus(order.status)}</span>
                  </div>
                )}
              </div>
            </div>
          ))}

          {!loading && selectedDayOrders.length === 0 && (
            <h2>Nenhuma entrega para {isToday ? 'hoje' : format(mapDate, 'dd/MM/yyyy')}.</h2>
          )}
          {loading && <div className="loadingData"><span>Carregando...</span></div>}
        </div>

        {/* ── Próximas Entregas ─────────────────────────────────────────────── */}
        <div className="sectionCard">
          <h2>
            Próximas Entregas — {format(addDays(mapDate, 1), 'dd/MM')} a{' '}
            {format(addDays(mapDate, 7), 'dd/MM/yyyy')}
          </h2>
          <div className="flex-container">
            <div><h3>Data</h3></div>
            <div><h3>Horário</h3></div>
            <div><h3>Endereço</h3></div>
            <div><h3>Contato</h3></div>
            <div><h3>Apartamento</h3></div>
            <div><h3>Cód. Porta</h3></div>
            <div><h3>Pedido</h3></div>
            <div><h3>Status</h3></div>
            <div className="item">Ação</div>
          </div>

          {!loading && nextOrders.length > 0 && nextOrders.map(order => (
            <div key={order.id} className="flex-container">
              <div>{convertDateString(order.deliveryDate)}</div>
              <div>{order.deliveryTime?.substr(0, 5) || '--:--'}</div>
              <div>
                {order.deliveryAddress?.[0]
                  ? `${order.deliveryAddress[0].street}, n${order.deliveryAddress[0].streetNumber}`
                  : 'Endereço não informado'}
              </div>
              <div>
                {order.contactName || order.contactPhone ? (
                  <Button
                    type="button"
                    className="button-plus"
                    onClick={() => setSelectedContact({ name: order.contactName, phone: order.contactPhone })}
                  >
                    <span>+</span>
                  </Button>
                ) : (
                  <span style={{ color: '#bbb' }}>-</span>
                )}
              </div>
              <div>{order.apartment || '-'}</div>
              <div>{order.deliveryAddress?.[0]?.doorCode || '-'}</div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {order.lunchs?.length
                  ? order.lunchs.map((item, index) => (
                    <div key={String(index)}>{index < 3 ? `${item.quantity} - ${item.order}` : '...'}</div>
                  ))
                  : <span>Sem itens</span>}
              </div>
              <div>
                <div className={confirmStatus(order.status)}>
                  <span>{formatStatus(order.status)}</span>
                </div>
              </div>
              <div>
                {order.status !== 'ENTREGUE' && order.status !== 'CANCELADA' ? (
                  <Button onClick={() => handleMarkAsDelivered(order.id)}>Entregue ✓</Button>
                ) : (
                  <Link to={`/entregas-do-dia-detalhe-delivery/${order.id}`}>
                    <Button className="button-plus"><span>+</span></Button>
                  </Link>
                )}
              </div>
            </div>
          ))}

          {!loading && nextOrders.length === 0 && <h2>Nenhuma entrega nos próximos 7 dias.</h2>}
          {loading && <div className="loadingData"><h4>Carregando...</h4></div>}
        </div>

        {/* ── Histórico de Entregas ─────────────────────────────────────────── */}
        <div className="sectionCard">
          <h2>Histórico de Entregas</h2>
          <div className="flex-container">
            <div><h3>Data da Entrega</h3></div>
            <div><h3>Horário</h3></div>
            <div><h3>Cliente</h3></div>
            <div><h3>Endereço</h3></div>
            <div><h3>Pedido</h3></div>
            <div><h3>Status</h3></div>
          </div>

          {!loading && historyOrders.length > 0 && historyOrders.map(order => (
            <div key={order.id} className="flex-container">
              <div>{convertDateString(order.deliveryDate)}</div>
              <div>{order.deliveryTime?.substr(0, 5) || '--:--'}</div>
              <div>{order.client}</div>
              <div>
                {order.deliveryAddress?.[0]
                  ? `${order.deliveryAddress[0].street}, n${order.deliveryAddress[0].streetNumber}`
                  : 'Endereço não informado'}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {order.lunchs?.length
                  ? order.lunchs.map((item, index) => (
                    <div key={String(index)}>{index < 3 ? `${item.quantity} - ${item.order}` : '...'}</div>
                  ))
                  : <span>Sem itens</span>}
              </div>
              <div>
                <div className={confirmStatus(order.status)}>
                  <span>{formatStatus(order.status)}</span>
                </div>
              </div>
            </div>
          ))}

          {!loading && historyOrders.length === 0 && (
            <h2>Nenhuma entrega no histórico ainda.</h2>
          )}
          {loading && <div className="loadingData"><span>Carregando...</span></div>}
        </div>
      </Section>

      {/* ── Popup: Trocar Senha ─────────────────────────────────────────────── */}
      {showChangePassword && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 999,
          }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 8,
              padding: 32,
              width: 360,
              maxWidth: '90%',
            }}
          >
            <h2 style={{ marginBottom: 16 }}>Trocar Senha</h2>
            <Form ref={passwordFormRef} onSubmit={handleChangePassword}>
              <div className="form-inputs" >
                <h5>Nova Senha*</h5>
                <Input type="password" name="newPassword" />
              </div>
              <div className="form-inputs" style={{ marginTop: 16 }}>
                <h5>Confirmar Nova Senha*</h5>
                <Input type="password" name="confirmPassword" />
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
                <Button type="submit" loading={changingPassword}>
                  Salvar
                </Button>
                <Button
                  type="button"
                  className="allItems"
                  onClick={() => setShowChangePassword(false)}
                >
                  Cancelar
                </Button>
              </div>
            </Form>
          </div>
        </div>
      )}

      {/* ── Popup: Contato do Destinatário ──────────────────────────────────── */}
      {selectedContact && (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 999,
          }}
          onClick={() => setSelectedContact(null)}
        >
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
          <div
            style={{
              background: '#fff',
              borderRadius: 8,
              padding: 32,
              width: 320,
              maxWidth: '90%',
            }}
            onClick={e => e.stopPropagation()}
          >
            <h2 style={{ marginBottom: 20 }}>Contato do Destinatário</h2>
            <div className="form-inputs">
              <h5>Nome</h5>
              <p style={{ marginTop: 4 }}>{selectedContact.name || 'Não informado'}</p>
            </div>
            <div className="form-inputs" style={{ marginTop: 16 }}>
              <h5>Telefone</h5>
              <p style={{ marginTop: 4 }}>
                {selectedContact.phone ? (
                  <a href={`tel:${selectedContact.phone.replace(/\s/g, '')}`}>
                    {selectedContact.phone}
                  </a>
                ) : (
                  'Não informado'
                )}
              </p>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 24 }}>
              <Button
                type="button"
                className="allItems"
                onClick={() => setSelectedContact(null)}
              >
                Fechar
              </Button>
            </div>
          </div>
        </div>
      )}
    </Main>
  );
};

export default DeliveryManDashBoard;
